// Docs: IMPLEMENTATION.md §3.2. This is the single HTTP facade; update that
// section when adding a new API group or changing auth/header conventions.
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { clearPersistedCache } from '../queryPersistence';
import { resetAnalytics, trackEvent } from '../analytics';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api/v1';

export const POOL_LABELS = ['pool:active', 'pool:next', 'pool:suspended', 'pool:backlog'];
export const PRIO_LABELS = ['prio:high', 'prio:mid', 'prio:low'];
export const FAV_LABEL = 'my-fav';
// Explicit "Passiv reicht" override — forces prio:low in the classifier
// (mirror of the fav star, which forces prio:high).
export const ZIEL_PASSIV_LABEL = 'ziel:passiv';
// Added by the backend to every word that came in through Nachschlagen.
export const LOOKUP_LABEL = 'nachgeschlagen';
// Regional-usage labels set by the classifier (Helvetisms / Swiss German,
// Zürich dialect, Austrian). Ordinary user-editable labels — see
// backend app/region_label.py. region:zh always comes with region:ch.
export const REGION_LABELS = ['region:ch', 'region:zh', 'region:at'];
// Union — reserved labels filtered from the user-editable tag editor. Pool is
// toggled via the Pool pills, prio is recomputed from the rubric, fav via
// the star button, ziel:passiv via the "Passiv reicht" toggle.
export const SYSTEM_LABELS = [...POOL_LABELS, ...PRIO_LABELS, FAV_LABEL, ZIEL_PASSIV_LABEL];

// Generate unique request ID
const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const shouldRedirectToLogin = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error?.code;
  return status === 401 || code === 'HTTP_401' || code === 'UNAUTHORIZED';
};

api.interceptors.request.use((config) => {
  // Add request ID for tracing
  const requestId = generateRequestId();
  config.headers['X-Request-ID'] = requestId;
  config.metadata = { requestId, startTime: Date.now() };

  // Add Sentry breadcrumb for API call
  Sentry.addBreadcrumb({
    category: 'api',
    message: `${config.method?.toUpperCase()} ${config.url}`,
    level: 'info',
    data: { requestId },
  });

  const token = localStorage.getItem('jwt');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Ensure request data is serializable (no circular references)
  if (config.data) {
    try {
      JSON.stringify(config.data);
    } catch (e) {
      console.error('Request data contains circular references, cleaning...', e);
      // Try to clean the data by creating a new object with only serializable values
      const cleaned = {};
      for (const [key, value] of Object.entries(config.data)) {
        try {
          // Handle primitive types directly
          if (value === null || value === undefined) {
            cleaned[key] = value;
          } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            cleaned[key] = value;
          } else if (Array.isArray(value)) {
            // Try to serialize array
            JSON.stringify(value);
            cleaned[key] = value;
          } else if (typeof value === 'object') {
            // Try to serialize object
            JSON.stringify(value);
            cleaned[key] = value;
          } else {
            // Convert to string if it's something else
            cleaned[key] = String(value);
          }
        } catch (err) {
          // Skip non-serializable values, but log more details
          console.warn(`Skipping non-serializable key: ${key}`, { value, error: err.message });
        }
      }
      config.data = cleaned;
    }
  }
  
  return config;
});

// Which flows are actually used vs. dead weight — tracked centrally here
// (rather than at every call site) so the list stays a single source of
// truth. Matched against the request path with the query string stripped;
// only successful (2xx) responses fire. Add an entry whenever a new key flow
// is worth watching for usage/deprecation decisions (see IMPLEMENTATION.md
// §3.6 and FEATURES.md for what each flow does).
//
// `props(data, requestBody)` (optional) derives event properties from the
// response body and the parsed request payload, so events carry enough
// context (lesson type, drill type) to break flows down in PostHog.
//
// Note: `lesson_started` is NOT in this list. POST /lessons only covers
// manually created lessons (plan-day lessons are created server-side), so
// creation here is tracked as `lesson_created`, and `lesson_started` fires
// from the lesson runner instead — see trackLessonStarted in analytics.js.
const KEY_FLOW_ENDPOINTS = [
  {
    method: 'post', pattern: /^\/lessons$/, event: 'lesson_created',
    props: (data) => ({ lesson_type: data?.lesson_type ?? null }),
  },
  {
    method: 'post', pattern: /^\/lessons\/[^/]+\/complete$/, event: 'lesson_completed',
    props: (data) => ({
      lesson_type: data?.lesson_type ?? null,
      source: data?.source ?? null,
      is_drill: data?.drill_type != null,
      drill_type: data?.drill_type ?? null,
      // Review → "weiter": the follow-up was created in the same request.
      followup_started: data?.followup != null,
    }),
  },
  {
    method: 'post', pattern: /^\/lessons\/[^/]+\/followup$/, event: 'followup_lesson_started',
    props: (data) => ({ lesson_type: data?.lesson_type ?? null }),
  },
  { method: 'post', pattern: /^\/words\/import$/, event: 'word_added' },
  { method: 'post', pattern: /^\/words\/lookup\/\d+\/add$/, event: 'word_added_from_lookup' },
  {
    method: 'post', pattern: /^\/drills\/create-lesson$/, event: 'drill_started',
    props: (data, requestBody) => ({ drill_type: requestBody?.drill_type ?? null }),
  },
  { method: 'post', pattern: /^\/text\/improve$/, event: 'text_improve_run' },
  { method: 'post', pattern: /^\/words\/podcast$/, event: 'podcast_generated' },
  {
    method: 'post', pattern: /^\/podcasts\/themes\/\d+\/generate$/, event: 'podcast_generated',
    props: () => ({ source: 'inbox_theme' }),
  },
];

const trackKeyFlow = (response) => {
  const config = response?.config;
  const method = config?.method?.toLowerCase();
  const path = config?.url?.split('?')[0];
  if (!method || !path) return;
  const match = KEY_FLOW_ENDPOINTS.find((e) => e.method === method && e.pattern.test(path));
  if (!match) return;
  let properties;
  if (match.props) {
    // Axios serializes the request body to a JSON string before the
    // interceptor sees it; parse it back for property extraction.
    let requestBody = config.data;
    if (typeof requestBody === 'string') {
      try { requestBody = JSON.parse(requestBody); } catch { requestBody = undefined; }
    }
    try {
      properties = match.props(response.data, requestBody);
    } catch {
      // Property extraction must never block the event itself.
    }
  }
  trackEvent(match.event, properties);
};

api.interceptors.response.use(
  (response) => {
    // Sliding session: the backend renews a day-old JWT on any successful
    // authenticated call, so an active user is never logged out.
    const refreshedToken = response.headers?.['x-refreshed-token'];
    if (refreshedToken && localStorage.getItem('jwt')) {
      localStorage.setItem('jwt', refreshedToken);
    }
    trackKeyFlow(response);
    // Add success breadcrumb with timing
    const metadata = response.config?.metadata;
    if (metadata) {
      const duration = Date.now() - metadata.startTime;
      Sentry.addBreadcrumb({
        category: 'api',
        message: `${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
        level: 'info',
        data: {
          requestId: metadata.requestId,
          status: response.status,
          duration: `${duration}ms`,
        },
      });
    }
    return response;
  },
  (error) => {
    // Add error breadcrumb
    const metadata = error.config?.metadata;
    const requestId = metadata?.requestId || error.response?.headers?.['x-request-id'];
    const serverRequestId = error.response?.data?.request_id;

    Sentry.addBreadcrumb({
      category: 'api',
      message: `API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      level: 'error',
      data: {
        requestId,
        serverRequestId,
        status: error.response?.status,
        errorCode: error.response?.data?.error?.code,
        errorMessage: error.response?.data?.error?.message,
      },
    });

    // For server errors (5xx), capture to Sentry
    if (error.response?.status >= 500) {
      Sentry.captureException(error, {
        tags: {
          requestId,
          serverRequestId,
          endpoint: error.config?.url,
        },
        extra: {
          responseData: error.response?.data,
        },
      });
    }

    if (shouldRedirectToLogin(error)) {
      localStorage.removeItem('jwt');
      clearPersistedCache();
      resetAnalytics();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Words API

export const userAPI = {
  getCurrentUser: async () => (await api.get('/me')).data,
};

export const wordsAPI = {
  getWord: async (id) => (await api.get(`/words/${id}`)).data,
  updateWord: async (id, data) => (await api.put(`/words/${id}`, data)).data,
  deleteWord: async (id) => (await api.delete(`/words/${id}`)).data,
  getSimilarWords: async (id, { top_k, threshold } = {}) =>
    (await api.get(`/words/${id}/similar`, { params: { top_k, threshold } })).data,
  compareWords: async (base_word_id, word_ids) =>
    (await api.post('/words/compare', { base_word_id, word_ids })).data,
  validateUserSentence: async (wordId, sentence) =>
    (await api.post(`/words/${wordId}/user-sentences/validate`, { sentence })).data,
  recomputeWord: async (id, payload = {}) =>
    (await api.post(`/words/${id}/recompute`, payload && Object.keys(payload).length ? payload : undefined)).data,
  recomputeWordFsrs: async (id, payload = {}) =>
    (await api.post(`/words/${id}/fsrs/recompute`, payload && Object.keys(payload).length ? payload : undefined)).data,
  // Rebuild only the collocation profile (Word.collocations) — an LRO.
  // scope: 'missing' (default: no profile yet / older version) or 'all'.
  // word_ids omitted = the whole word list: that is the backfill for words
  // added before the feature existed.
  // Whole-list backfill of region labels for words the classifier has not
  // judged yet. Returns an OperationStartResponse (skipped when none).
  backfillRegions: async () => (await api.post('/words/regions/backfill', {})).data,
  // wordIds omitted = whole list; otherwise only those words (try a subset first).
  backfillStyle: async (wordIds) => (await api.post('/words/style/backfill', wordIds ? { word_ids: wordIds } : {})).data,
  refreshCollocations: async ({ word_ids, scope = 'missing' } = {}) =>
    (await api.post('/words/collocations/refresh', word_ids ? { word_ids, scope } : { scope })).data,
  getWodHistory: async ({ limit } = {}) => {
    const params = {};
    if (limit) params.limit = limit;
    return (await api.get('/words/wod/history', { params })).data;
  },
  pickWod: async (wordId) =>
    (await api.post('/words/wod/pick', { word_id: wordId })).data,
  resetLastWod: async () => (await api.delete('/words/wod/pick/last')).data,
  getWeekPicks: async ({ labels, excludeLabels, types, limit, days } = {}) => {
    const params = {};
    if (labels?.length) params.labels = labels;
    if (excludeLabels?.length) params.exclude_labels = excludeLabels;
    if (types?.length) params.types = types;
    if (limit) params.limit = limit;
    if (days) params.days = days;
    return (await api.get('/words/week-picks', { params, paramsSerializer: { indexes: null } })).data;
  },
  searchWords: async ({ search, offset, limit, labels, excludeLabels, types, fsrsStates, lessonStatuses, poolStatuses, lessonType, sortBy, sortDir, randomSeed, includeItems } = {}) => {
    const params = { offset: offset ?? 0, limit: limit ?? 50 };
    if (includeItems === false) params.include_items = false;
    if (search) params.search = search;
    if (labels?.length) params.labels = labels;
    if (excludeLabels?.length) params.exclude_labels = excludeLabels;
    if (types?.length) params.types = types;
    if (fsrsStates?.length) params.fsrs_states = fsrsStates;
    if (lessonStatuses?.length) params.lesson_statuses = lessonStatuses;
    if (poolStatuses?.length) params.pool_statuses = poolStatuses;
    if (lessonType) params.lesson_type = lessonType;
    if (sortBy) params.sort_by = sortBy;
    if (sortDir) params.sort_dir = sortDir;
    if (randomSeed != null) params.random_seed = randomSeed;
    return (await api.get('/words/search', { params, paramsSerializer: { indexes: null } })).data;
  },
  // Wörter search box autocomplete — the user's own words only (no external call).
  // `external` asks for Wiktionary headwords under the owned words; the
  // backend only honours it while the "Externe Suche" preference is on.
  suggest: async (q, { external = false } = {}) =>
    (await api.get('/words/suggest', { params: external ? { q, external: true } : { q } })).data,
  generateAudio: async (wordId) => (await api.post(`/words/${wordId}/audio/generate`)).data,
  getAudioBlob: async (wordId) => (await api.get(`/words/${wordId}/audio`, { responseType: 'blob' })).data,
  // Provenance + encoding of the stored clip (debug panel); 404 when none.
  getAudioInfo: async (wordId) => (await api.get(`/words/${wordId}/audio/info`)).data,
  hintMatch: async ({ tokens, sequences, excludeWordId, targetWordId, meaningWords, neighborsOnly }, { signal } = {}) =>
    (await api.post('/words/hint-match', {
      tokens,
      sequences: sequences || [],
      exclude_word_id: excludeWordId,
      target_word_id: targetWordId || excludeWordId,
      meaning_words: meaningWords || [],
      neighbors_only: !!neighborsOnly,
    }, signal ? { signal } : undefined)).data,
  // Surface forms of the target word + its semantic neighbours, fetched once
  // per question so the green/red highlight resolves locally instead of
  // hitting /words/hint-match on every keystroke.
  hintForms: async (wordId) => (await api.get(`/words/${wordId}/hint-forms`)).data,
  generatePodcast: async (payload) => (await api.post('/words/podcast', payload)).data,
  // PDF export. Resolves to { blob, filename } — the filename comes from the
  // response's Content-Disposition so the download matches what the backend
  // named the file. An error body arrives as a Blob too (responseType applies
  // to every status), so the caller reads it back as text to show the detail.
  exportPdf: async (payload) => {
    const response = await api.post('/words/export/pdf', payload, { responseType: 'blob' });
    const disposition = response.headers?.['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    return { blob: response.data, filename: match ? match[1] : 'wortschatz.pdf' };
  },
  hasAudio: async (wordId) => {
    try {
      await api.head(`/words/${wordId}/audio`);
      return true;
    } catch (e) {
      if (e.response && e.response.status === 404) return false;
      // For other errors, assume not present
      return false;
    }
  },
  getWordVerdicts: async (wordId, limit = 10) =>
    (await api.get(`/words/${wordId}/verdicts`, { params: { limit } })).data,
};

export const lessonsAPI = {
  getLessons: async () => (await api.get('/lessons')).data,
  startLesson: async (data) => (await api.post('/lessons', data)).data,
  getFollowUpLessons: async () => (await api.get('/lessons/followups')).data,
  startFollowUpLesson: async (lessonId) => (await api.post(`/lessons/${lessonId}/followup`)).data,
  getLesson: async (id) => (await api.get(`/lessons/${id}`)).data,
  deleteLesson: async (id) => (await api.delete(`/lessons/${id}`)).data,
  // Debug: replace the lesson with a freshly generated copy (same words,
  // source and started_at, so a plan_day lesson stays in today's plan).
  regenerateLesson: async (id) => (await api.post(`/lessons/${id}/regenerate`)).data,
  // `startFollowup` creates the follow-up in the same request; the response's
  // `followup` is { lesson_id, operation_id, name, lesson_type, total_questions } or null.
  completeLesson: async (id, { startFollowup = false } = {}) => (
    await api.post(`/lessons/${id}/complete`, startFollowup ? { start_followup: true } : undefined)
  ).data,
  getFollowUpPreview: async (id) => (await api.get(`/lessons/${id}/followup-preview`)).data,
  submitAnswer: async (id, { answer, question_id }) => (await api.post(`/lessons/${id}/answer`, { answer, question_id })).data,
  getLessonWords: async (id) => (await api.get(`/lessons/${id}`)).data.words,
  getAllLessonQuestions: async (id) => (await api.get(`/lessons/${id}/questions/all`)).data,
  // `source` / `client` only feed the dialogue debug log.
  updateQuestionFsrsRating: async (lessonId, questionId, { fsrs_rating, source, client } = {}) =>
    (await api.post(`/lessons/${lessonId}/questions/${questionId}/fsrs-rating`, {
      fsrs_rating,
      ...(source ? { source } : {}),
      ...(client ? { client } : {}),
    })).data,
  submitAnswersBatch: async (lessonId, answers) =>
    (await api.post(`/lessons/${lessonId}/answers/batch`, { answers })).data,
  requeueQuestion: async (lessonId, questionId) =>
    (await api.post(`/lessons/${lessonId}/answers/requeue`, { question_id: questionId })).data,
  // Adaptive interlocutor reaction between two dialogue turns. user_answer
  // travels in the body — this call races the answer batch.
  dialogueBridge: async (lessonId, { question_id, user_answer, next_question_id }) =>
    (await api.post(`/lessons/${lessonId}/dialogue-bridge`, { question_id, user_answer, next_question_id })).data,
  // Self-repair of a dialogue reply graded AGAIN: feedback only, never
  // changes the rating.
  // Client-side steps of a dialogue turn for the debug log (fire-and-forget).
  dialogueLog: async (lessonId, events) =>
    (await api.post(`/lessons/${lessonId}/dialogue-log`, { events })).data,
  dialogueRepair: async (lessonId, { question_id, user_answer }) =>
    (await api.post(`/lessons/${lessonId}/dialogue-repair`, { question_id, user_answer })).data,
  // Experimenteller Dialog: one follow-up step of a turn — kind is
  // 'guided' (new try after absent/other_frame), 'give_up', 'fix' (single
  // fix try after used_wrongly; empty answer = skipped) or 'follow_up'
  // (addition after incomplete). The server owns the rating.
  dialogueGuided: async (lessonId, { question_id, kind, user_answer = '', assist }) =>
    (await api.post(`/lessons/${lessonId}/dialogue-guided`, { question_id, kind, user_answer, assist })).data,
  // questionId is the *original* question; the server finds its retry clone.
  cancelRequeue: async (lessonId, questionId) =>
    (await api.delete(`/lessons/${lessonId}/answers/requeue/${questionId}`)).data,
};

export const questionsAPI = {
  // NB: there is no answer-audio endpoint. `/questions/{id}/audio` and its
  // table were retired in 20260918 — unwritten since March, unread by any
  // component. Meaning-audio (the prompt narration) is the live feature.
  getQuestionMeaningAudioBlob: async (questionId) => (await api.get(`/questions/${questionId}/meaning-audio`, { responseType: 'blob' })).data,
  hasQuestionMeaningAudio: async (questionId) => {
    try {
      await api.head(`/questions/${questionId}/meaning-audio`);
      return true;
    } catch (e) {
      if (e.response && e.response.status === 404) return false;
      // For other errors, assume not present
      return false;
    }
  },
  generateQuestionMeaningAudio: async (questionId) => (await api.post(`/questions/${questionId}/meaning-audio/generate`)).data,
  generateQuestionMeaningAudioBulk: async (questionIds) => (await api.post('/questions/meaning-audio/generate', { question_ids: questionIds })).data,
};

export const statsAPI = {
  getStats: async (days = 90) => (await api.get('/stats', { params: { days } })).data,
  getFsrsStats: async ({ labels = [], excludeLabels = [], types = [], limit = 20 } = {}) => {
    const params = { limit };
    if (labels && labels.length > 0) {
      params.labels = labels;
    }
    if (excludeLabels && excludeLabels.length > 0) {
      params.exclude_labels = excludeLabels;
    }
    if (types && types.length > 0) {
      params.types = types;
    }
    return (await api.get('/stats/fsrs', { params })).data;
  },
  getFsrsSimulation: async ({ labels = [], excludeLabels = [], types = [], new_per_day, review_per_day } = {}) => {
    const params = {};
    if (labels && labels.length > 0) {
      params.labels = labels;
    }
    if (excludeLabels && excludeLabels.length > 0) {
      params.exclude_labels = excludeLabels;
    }
    if (types && types.length > 0) {
      params.types = types;
    }
    if (typeof new_per_day === 'number') {
      params.new_per_day = new_per_day;
    }
    if (typeof review_per_day === 'number') {
      params.review_per_day = review_per_day;
    }
    return (await api.get('/stats/fsrs/progression', { params })).data;
  },
  getCueStats: async () => (await api.get('/stats/cues')).data,
  getPoolOverview: async () => (await api.get('/stats/pool-overview')).data,
  getLearningProgress: async () => (await api.get('/stats/learning-progress')).data,
  getPoolProgression: async (maxDays = 365) =>
    (await api.get('/stats/pool-progression', { params: { max_days: maxDays } })).data,
  getPoolActiveWords: async () => (await api.get('/stats/pool-active-words')).data,
  // Wortschatz-Punkte: the whole vocabulary integrated into one score, plus
  // today's change and what skipping today would cost. Calling it also opens
  // today's ledger entry on the backend.
  getPoints: async () => (await api.get('/stats/points')).data,
  getPointsTracks: async (days = 90) =>
    (await api.get('/stats/points/tracks', { params: { days } })).data,
};

export const usageAPI = {
  getUsageStats: async (days = null) => (await api.get('/usage/stats', { params: days ? { days } : {} })).data,
  getUsageHistory: async (limit = 100) => (await api.get('/usage/history', { params: { limit } })).data,
  getBreakdown: async (days = null) => (await api.get('/usage/breakdown', { params: days ? { days } : {} })).data,
};

// API key store + Vertrauenskreis sharing. Plaintext only ever appears in the
// reveal() response — discard it from React state within 30 s.
export const apiKeysAPI = {
  list: async () => (await api.get('/keys')).data,
  create: async (body) => (await api.post('/keys', body)).data,
  get: async (id) => (await api.get(`/keys/${id}`)).data,
  update: async (id, body) => (await api.put(`/keys/${id}`, body)).data,
  delete: async (id) => (await api.delete(`/keys/${id}`)).data,
  validate: async (id) => (await api.post(`/keys/${id}/validate`)).data,
  reveal: async (id) => (await api.post(`/keys/${id}/reveal`)).data,
  // Probe an admin secret to list its accessible OpenAI projects. Used by
  // AddApiKeyDialog so the user picks from a dropdown. Secret is sent but
  // never persisted by this endpoint.
  probeOpenAIProjects: async (secret) =>
    (await api.post('/keys/probe-openai-projects', { secret })).data,
  // Same but for an *existing* admin key — server uses the stored secret.
  probeProjectsForKey: async (id) =>
    (await api.post(`/keys/${id}/probe-projects`)).data,
  // Per-key ElevenLabs subscription snapshot (plan, characters used / limit,
  // reset). Owner-only.
  elevenlabsSubscriptionForKey: async (id) =>
    (await api.get(`/keys/${id}/elevenlabs-subscription`)).data,
  // Owner-only: open this key to (or close it from) the whole Vertrauenskreis.
  // Returns { friend_count, activated_for, deactivated_for } so the UI can say
  // what the switch actually did.
  setSharing: async (id, shared) =>
    (await api.put(`/keys/${id}/sharing`, { shared_with_friends: shared })).data,
};

// Vertrauenskreis — the directed circle of people who may use the keys you
// share. One overview() call powers the whole panel; every mutation returns
// the edge plus what it did to the friend's active-key pointers.
export const keyFriendsAPI = {
  overview: async () => (await api.get('/key-friends')).data,
  // Owner adds somebody (accepted at once — you are the one granting).
  add: async (email) => (await api.post('/key-friends', { email })).data,
  // Ask to be let into somebody's circle. body: { email, message? }
  request: async (body) => (await api.post('/key-friends/requests', body)).data,
  withdrawRequest: async (id) => (await api.delete(`/key-friends/requests/${id}`)).data,
  accept: async (id) => (await api.post(`/key-friends/${id}/accept`)).data,
  decline: async (id) => (await api.post(`/key-friends/${id}/decline`)).data,
  // Owner revokes, or friend leaves — same endpoint, either side.
  remove: async (id) => (await api.delete(`/key-friends/${id}`)).data,
};

export const enumsAPI = {
  getTypes: async () => (await api.get('/enums/types')).data,
};

export const languagesAPI = {
  getLanguages: async () => (await api.get('/languages')).data,
};

export const preferencesAPI = {
  getPreferences: async () => (await api.get('/user/preferences')).data,
  updatePreferences: async (preferences) => (await api.put('/user/preferences', preferences)).data,
  // The user-day window: 24h starting at 03:00 local. Drives "today" /
  // streak boundaries on the frontend so they match the backend.
  getDayWindow: async () => (await api.get('/user/day-window')).data,
};

// Debug / Version API
export const debugAPI = {
  getVersionInfo: async () => (await api.get('/debug/version')).data,
  getPoolInfo: async () => (await api.get('/debug/pool-info')).data,
  resetPlannedDate: async () => (await api.post('/debug/reset-planned-date')).data,
  getGoalHistory: async () => (await api.get('/debug/goal-history')).data,
  createGoalHistoryEntry: async (body) => (await api.post('/debug/goal-history', body)).data,
  updateGoalHistoryEntry: async (id, body) => (await api.put(`/debug/goal-history/${id}`, body)).data,
  deleteGoalHistoryEntry: async (id) => (await api.delete(`/debug/goal-history/${id}`)).data,
  submitBugReport: async (body) => (await api.post('/debug/bug-reports', body)).data,
  listBugReports: async () => (await api.get('/debug/bug-reports')).data,
  listPromos: async () => (await api.get('/debug/promos')).data,
  resetPromo: async (promoKey) => (await api.post(`/debug/promos/${encodeURIComponent(promoKey)}/reset`)).data,
};

// Operations API
export const operationsAPI = {
  start: async (name, payload) => (await api.post('/operations/start', { name, payload })).data,
  get: async (id) => (await api.get(`/operations/${id}`)).data,
  list: async (params = {}) => (await api.get('/operations/list', { params })).data,
  getJobs: async (id) => (await api.get(`/operations/${id}/jobs`)).data,
  getTasks: async (id) => (await api.get(`/operations/${id}/tasks`)).data,
  updateStatus: async (id, status) => (await api.patch(`/operations/${id}`, { status })).data,
  importWords: async ({ labels, words, hint = null }) =>
    (await api.post('/words/import', hint ? { labels, words, hint } : { labels, words })).data,
  reclassifyWords: async ({ word_ids, hint = null, scope = null }) => {
    const payload = { word_ids };
    if (hint) {
      payload.hint = hint;
    }
    if (scope) {
      payload.scope = scope;
    }
    return (await api.post('/words/reclassify', payload)).data;
  },
  generateAudioBulk: async ({ word_ids }) => (await api.post('/words/audio/regenerate', { word_ids })).data,
  recomputeFrequency: async ({ word_ids }) => (await api.post('/words/recompute-frequency', { word_ids })).data,
  scanDuplicates: async (params = {}) => (await api.post('/words/deduplicate/scan', params)).data,
  mergeDuplicates: async (groups) => (await api.post('/words/deduplicate/merge', { groups })).data,
};

// Podcasts API
export const podcastsAPI = {
  list: async () => (await api.get('/podcasts')).data,
  get: async (id) => (await api.get(`/podcasts/${id}`)).data,
  getTranscript: async (id) => (await api.get(`/podcasts/${id}/transcript`)).data,
  delete: async (id) => (await api.delete(`/podcasts/${id}`)).data,
  audioUrl: (id) => `${API_BASE_URL}/podcasts/${id}/audio`,
  getElevenLabsUsage: async (lengthMinutes, ttsModel) => {
    const params = {};
    if (lengthMinutes) params.length_minutes = lengthMinutes;
    if (ttsModel) params.tts_model = ttsModel;
    return (await api.get('/podcasts/elevenlabs-usage', { params })).data;
  },
  getPresets: async () => (await api.get('/podcasts/presets')).data,
  // One-tap episode from the Inbox's thematic nudge: the suggestion already
  // carries the words and the angle, so there is nothing to pass.
  generateFromTheme: async (suggestionId) =>
    (await api.post(`/podcasts/themes/${suggestionId}/generate`)).data,
  // "Find a theme now": runs the Inbox's theme hunt on demand (ranked
  // clusters, falling back to all active words) and returns the same payload
  // as the Inbox's podcast_theme row. 404 = nothing coheres.
  suggestTheme: async () => (await api.post('/podcasts/themes/suggest')).data,
  validateVoice: async (voiceId) =>
    (await api.get('/podcasts/validate-voice', { params: { voice_id: voiceId } })).data,
};

// Voices API — the voice libraries of all three TTS providers, all of it
// feeding Settings → Audio. See backend/app/api/voices.py.
//
// ElevenLabs voices carry a public `preview_url`; neither Google nor Azure has
// an equivalent, so auditioning one of their voices means synthesizing a
// sample. That comes back as an audio blob behind JWT auth, not a URL an
// <audio src=…> could load.
//
// The two synthesized providers share one endpoint shape, so they share one
// pair of helpers here rather than growing a copy each time a backend is
// added. `provider` is the same string the backend uses in tts_settings.
const SYNTH_VOICE_PATH = { google_tts: 'google', azure_tts: 'azure' };

export const voicesAPI = {
  list: async () => (await api.get('/voices')).data,
  listSynth: async (provider) =>
    (await api.get(`/voices/${SYNTH_VOICE_PATH[provider]}`)).data,
  previewSynth: async (provider, voice) =>
    (await api.post(
      `/voices/${SYNTH_VOICE_PATH[provider]}/preview`,
      { voice },
      { responseType: 'blob' },
    )).data,
};

// Cues API — first-class prompt-side artifacts (picture, sound, text).
// See backend/app/api/cues.py and backend/app/cue_kinds.py.
//
// NOTE: cue assets are protected by JWT auth, so we can't drop a raw
// /cues/{id}/asset URL into <img src=…> — browsers don't attach the
// Authorization header to <img> requests. Use `getAssetBlob` and
// `URL.createObjectURL` to render assets instead.
export const cuesAPI = {
  listForWord: async (wordId, { onlyEnabled = false } = {}) =>
    (await api.get(`/words/${wordId}/cues`, { params: { only_enabled: onlyEnabled } })).data,
  getAssetBlob: async (cueId) =>
    (await api.get(`/cues/${cueId}/asset`, { responseType: 'blob' })).data,
  patch: async (cueId, body) => (await api.patch(`/cues/${cueId}`, body)).data,
  regenerate: async (cueId) => (await api.post(`/cues/${cueId}/regenerate`)).data,
  regenerateForWord: async (wordId, kind) =>
    (await api.post(`/words/${wordId}/cues/regenerate`, null, { params: { kind } })).data,
  addKind: async (wordId, kind) =>
    (await api.post(`/words/${wordId}/cues`, { kind })).data,
  // Generates (or re-generates) the word's Merkbild — the unified pencil
  // scene the wallpaper prefers over the plain picture cue. Async: returns
  // an OperationStartResponse; the finished scene lands as the word's
  // 'wallpaper' cue and the inbox surfaces a merkbild_ready item.
  generateWallpaperScene: async (wordId) =>
    (await api.post(`/words/${wordId}/wallpaper-scene`)).data,
  // Merkbilder for a set of words (the week's picks) in one background
  // operation; words that already have a scene are skipped server-side.
  generateWallpaperScenesBatch: async (wordIds) =>
    (await api.post('/wallpaper-scenes/batch', { word_ids: wordIds })).data,
};

// Synonym Clusters API
export const synonymClustersAPI = {
  discover: async (params = {}) => (await api.post('/synonym-clusters/discover', params)).data,
  list: async (status = null) => (await api.get('/synonym-clusters', { params: status ? { status } : {} })).data,
  get: async (id) => (await api.get(`/synonym-clusters/${id}`)).data,
  update: async (id, data) => (await api.put(`/synonym-clusters/${id}`, data)).data,
  generateNuance: async (id) => (await api.post(`/synonym-clusters/${id}/nuance`)).data,
  delete: async (id) => (await api.delete(`/synonym-clusters/${id}`)).data,
  resetCandidates: async () => (await api.delete('/synonym-clusters/candidates')).data,
  getForWord: async (wordId) => (await api.get(`/words/${wordId}/clusters`)).data,
};

// Text Improve API
export const textImproveAPI = {
  improve: async (text) =>
    (await api.post('/text/improve', { text })).data,
  save: async (data) =>
    (await api.post('/text/improve/save', data)).data,
  list: async () =>
    (await api.get('/text/improves')).data,
  get: async (id) =>
    (await api.get(`/text/improves/${id}`)).data,
  delete: async (id) =>
    (await api.delete(`/text/improves/${id}`)).data,
  updateTitle: async (id, title) =>
    (await api.patch(`/text/improves/${id}/title`, { title })).data,
  updateSuggestionStatus: async (id, status) =>
    (await api.patch(`/text/improve-suggestions/${id}/status`, { status })).data,
  getWordTextExamples: async (wordId) =>
    (await api.get(`/words/${wordId}/text-examples`)).data,
};

// Genus Trainer API
export const genusAPI = {
  getTiers: async (drillType) => (await api.get('/genus/tiers', { params: drillType ? { drill_type: drillType } : {} })).data,
  getProgress: async (tier, drillType) => (await api.get('/genus/progress', { params: { tier, ...(drillType ? { drill_type: drillType } : {}) } })).data,
  getStats: async () => (await api.get('/genus/stats')).data,
  getSuffixRules: async (tier) => (await api.get('/genus/suffix-rules', { params: tier ? { tier } : {} })).data,
  recordBossDefeat: async (tier, drillType) => (await api.post('/genus/boss-defeat', { tier }, { params: drillType ? { drill_type: drillType } : {} })).data,
  getHardestGenus: async (params = {}) => (await api.get('/genus/hardest-recent', { params })).data,
  getHardestPlural: async (params = {}) => (await api.get('/genus/hardest-plural-recent', { params })).data,
};

// Grammar Drills API
export const drillAPI = {
  createLesson: async (params) =>
    (await api.post('/drills/create-lesson', params)).data,
  getStats: async (drillType) =>
    (await api.get(`/drills/stats/${drillType}`)).data,
  getDetails: async (drillType) =>
    (await api.get(`/drills/details/${drillType}`)).data,
};

// Inbox API — aggregated daily feed (Word of the Day, Words of the Week,
// Plan Day prompt, today's plan_day lessons, custom + drill lessons).
export const inboxAPI = {
  get: async () => (await api.get('/inbox')).data,
  dismiss: async (itemKey) =>
    (await api.post('/inbox/dismiss', { item_key: itemKey })).data,
  snooze: async (itemKey, snoozedUntil) =>
    (await api.post('/inbox/snooze', { item_key: itemKey, snoozed_until: snoozedUntil })).data,
  pin: async (itemKey, pinned) =>
    (await api.post('/inbox/pin', { item_key: itemKey, pinned })).data,
};

// Nebenspur — goal-free cards for the user's chosen labels.
// Docs: FEATURES.md "Nebenspur". `start(label)` resumes today's open session
// for that label or builds a new one (due first, then new); 404 when nothing
// is due or new.
export const sidetrackAPI = {
  getLabels: async () => (await api.get('/sidetrack/labels')).data,
  setLabels: async (labels) => (await api.put('/sidetrack/labels', { labels })).data,
  start: async (label) => (await api.post('/sidetrack/start', { label })).data,
};

// Word lookup ("Nachschlagen") — dictionary entries for words not in the list.
// Docs: FEATURES.md "Nachschlagen"; IMPLEMENTATION.md "Word lookup".
// `stream` is the only non-axios call in this facade: it reads the NDJSON
// stream so each source panel renders the moment its source replies.
export const lookupAPI = {
  lookup: async (q, { force = false } = {}) =>
    (await api.get('/words/lookup', { params: force ? { q, force: true } : { q } })).data,
  suggest: async (q) => (await api.get('/words/lookup/suggest', { params: { q } })).data,
  enrich: async (body) => (await api.post('/words/lookup/enrich', body)).data,
  // `sense` = the picked Bedeutung (gloss) → classifier hint; the backend
  // appends the `nachgeschlagen` label and a pool label itself.
  add: async (logId, { labels = [], seed = null, sense = null } = {}) =>
    (await api.post(`/words/lookup/${logId}/add`, { labels, seed, sense })).data,
  // Calls onEvent(line) for every NDJSON line as it arrives; resolves when the
  // stream ends. Pass an AbortSignal to cancel when the query changes.
  stream: async (q, { force = false, signal, onEvent } = {}) => {
    const params = new URLSearchParams({ q });
    if (force) params.set('force', 'true');
    const token = localStorage.getItem('jwt');
    const headers = { Accept: 'application/x-ndjson' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE_URL}/words/lookup/stream?${params}`, { headers, signal });
    if (!res.ok) {
      const err = new Error(`lookup stream failed: ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const emit = (line) => {
      const trimmed = line.trim();
      if (trimmed) onEvent?.(JSON.parse(trimmed));
    };
    if (!res.body || typeof res.body.getReader !== 'function') {
      (await res.text()).split('\n').forEach(emit);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n')) >= 0) {
        emit(buf.slice(0, idx));
        buf = buf.slice(idx + 1);
      }
    }
    emit(buf);
  },
};

// Küche language game (Entdecken): one instruction → NPC actions + feedback
export const gameAPI = {
  command: async (payload) => (await api.post('/game/command', payload)).data,
};

// Speech-to-text fallback for the Sprechen prototype (home-screen app, where
// the browser's own recognition is blocked). `vocabulary` is the whole
// round's word list — a spelling hint, never the current word alone.
export const speechAPI = {
  transcribe: async (blob, vocabulary = []) => {
    const type = String(blob?.type || 'audio/webm').split(';')[0];
    const ext = type.includes('mp4') || type.includes('m4a') || type.includes('aac') ? 'mp4'
      : type.includes('ogg') ? 'ogg' : type.includes('wav') ? 'wav' : 'webm';
    const form = new FormData();
    form.append('audio', blob, `speech.${ext}`);
    form.append('vocabulary', JSON.stringify(vocabulary));
    // The instance default is JSON, which would make axios serialise the
    // FormData; multipart lets the browser set the boundary.
    return (await api.post('/speech/transcribe', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
};

// Re-activation onboarding (wizard for a full, unused word list)
export const onboardingAPI = {
  getStatus: async () => (await api.get('/onboarding/status')).data,
  wordSuggestions: async () => (await api.get('/onboarding/word-suggestions')).data,
  preview: async (answers) => (await api.post('/onboarding/preview', answers)).data,
  seen: async () => (await api.post('/onboarding/seen')).data,
  complete: async (payload) => (await api.post('/onboarding/complete', payload)).data,
  dismiss: async () => (await api.post('/onboarding/dismiss')).data,
};

// Word Pool API
// POST /pool/plan-day is the one call this facade retries itself.
//
// It is the slowest write in the app (FSRS simulation + lesson creation:
// 2-30 s in production, more on a cold container) and it is fired from the
// Heute inbox of an installed iOS PWA. iOS Safari kills in-flight XHRs when
// the app is backgrounded, frozen or simply navigated away from mid-request
// — the edge proxy logs those as 499 "client has closed the request before
// the server could send a response" while the server runs the planning to
// completion. Axios surfaces that as an error with no `response`, which the
// Inbox used to report as a plain failure even though the day *had* been
// planned (observed 7×, always iOS, never with a 5xx).
//
// Retrying is safe because the endpoint is idempotent per user-day
// (IMPLEMENTATION.md §2.7.1): once a plan exists it echoes it, and while one
// is being written it answers 409. Both of those are retry signals here —
// 409 means the aborted attempt is very likely still finishing server-side —
// so the delays have to outlast a slow plan, not just a slow network.
const PLAN_DAY_RETRY_DELAYS_MS = [1500, 3000, 6000, 12000, 20000];

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A response-less error is an aborted/dropped request, not a verdict; a 409
// is the lease held by (most likely) our own aborted attempt.
const isRetriablePlanDayError = (error) =>
  !error?.response || error.response.status === 409;

export const poolAPI = {
  getSystemLabels: async () => (await api.get('/pool/system-labels')).data,
  getStatus: async () => (await api.get('/pool/status')).data,
  move: async (word_ids, target) =>
    (await api.post('/pool/move', { word_ids, target })).data,
  previewPlanDay: async () => (await api.get('/pool/plan-day/preview')).data,
  autoManage: async () => (await api.post('/pool/auto-manage')).data,
  planDay: async () => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        return (await api.post('/pool/plan-day')).data;
      } catch (error) {
        if (
          attempt >= PLAN_DAY_RETRY_DELAYS_MS.length ||
          !isRetriablePlanDayError(error)
        ) {
          throw error;
        }
        await delay(PLAN_DAY_RETRY_DELAYS_MS[attempt]);
      }
    }
  },
  getNextSuggestions: async (limit = 20) =>
    (await api.get('/pool/next-suggestions', { params: { limit } })).data,
  rejectNextSuggestions: async (word_ids) =>
    (await api.post('/pool/next-suggestions/reject', { word_ids })).data,
};

// Time Tracking API
export const timeTrackingAPI = {
  start: async (lessonId, sessionType) => (await api.post('/time-tracking/start', { 
    lesson_id: lessonId, 
    session_type: sessionType 
  })).data,
  stop: async (sessionId, activeSeconds) => (await api.post(`/time-tracking/stop/${sessionId}`, {
    active_duration_seconds: activeSeconds != null ? Math.round(activeSeconds) : undefined,
  })).data,
  getSessions: async (lessonId = null, days = 7) => (await api.get('/time-tracking/sessions', {
    params: { lesson_id: lessonId, days }
  })).data,
};

export const oauthAPI = {
  getConsentInfo: async (req) => (await api.get('/oauth/consent-info', { params: { req } })).data,
  postConsent: async (req, decision) => (await api.post('/oauth/consent', { req, decision })).data,
  // MCP clients (e.g. Claude.ai) connected to this account; disconnecting cuts
  // them off on their next request.
  listClients: async () => (await api.get('/oauth/clients')).data,
  disconnectClient: async (clientId) => (await api.delete(`/oauth/clients/${encodeURIComponent(clientId)}`)).data,
};
